import React, {useEffect} from "react";
import {useTranslation} from "react-i18next";
import EventTemplate from "../../../stores/domainStore/pages/models/eventTemplate";
import CheckBox from "../../atoms/checkBox/checkBox";
import "./createEditEventTemplateTemplate.sass";
import {observer} from "mobx-react-lite";
import CreateEditForm from "../../molecules/createEditForm/createEditForm";
import EventTemplateDurationEdit from "../../organisms/eventTemplateDurationEdit/eventTemplateDurationEdit";
import Skeleton from "react-loading-skeleton";
import SingleSelect from "../../molecules/multiselect/singleSelect";
import ButtonIcon from "../../atoms/button/buttonIcon";
import TextBox from "../../atoms/input/textBox";
import Label from "../../atoms/label/label";
import {RoutePaths} from "../../../shared/routePaths";
import {useHistory} from "react-router";
import {Navigation} from "../../../utils/navigation";
import ImageSelector from "../../molecules/imageSelector/imageSelector";
import {BreadcrumbItem} from "../../atoms/breadcrumb/breadcrumb";
import {QueryParams} from "../../../shared/enums/queryParams";
import {
  eventCalendarTypeDisplay,
  eventCalendarTypeFromCatalogueParam,
  hasEnhancedFeatures
} from "../../../shared/eventCalendarTypeHelper";
import {runInAction} from "mobx";

/**
 * Properties that define the CreateEditEventTemplate.
 */
export interface ICreateEditEventTemplateTemplateProps {
  /**
   * The event template to be edited.
   */
  eventTemplate: EventTemplate;
  /**
   * Whether the categories are loading.
   */
  categoriesLoading: boolean;
  /**
   * Whether the image is loading.
   */
  imageLoading: boolean;
  /**
   * Whether the template is used to edit an existing event template.
   */
  isEditing: boolean;
  /**
   * Whether the template is in a loading state.
   */
  isLoading: boolean;
  /**
   * Whether the template is in the saving state.
   */
  isSaving: boolean;
  /**
   * The callback to publish an event template.
   */
  onPublish: () => Promise<void>;
  /**
   * The callback to handle the image selected operation.
   */
  onImageSelected: (file: File) => Promise<void>;

  /**
   * Indicates whether the form is dirty or not
   */
  hasUnsavedChanges?: boolean;
}

/**
 * The template for create or edit an event template.
 */
const CreateEditEventTemplateTemplate: React.FC<
  ICreateEditEventTemplateTemplateProps
> = ({
  eventTemplate,
  categoriesLoading,
  imageLoading,
  isEditing,
  isLoading,
  isSaving,
  hasUnsavedChanges,
  onPublish,
  onImageSelected
}) => {
  const history = useHistory();
  const {t} = useTranslation(["translation", "eventTemplate"]);

  const getImageValidationMessage = (): string => {
    if (eventTemplate.image.hasExceedsFileSizeLimit)
      return t("translation:controls.image.overFileSizeLimit", {
        maxImageFileSizeMegabytes: 5
      });
    else if (eventTemplate.image.isInvalidFileFormat)
      return t("translation:controls.image.invalidFileFormat");
    else if (eventTemplate.image.hasExceedsResolution)
      return t("translation:controls.image.aboveMaxResolution", {
        maxWidthAndHeight: 4000
      });
    else if (!eventTemplate.image.file)
      return t("translation:controls.image.error");
    return "";
  };

  const pageTitle = isEditing
    ? eventTemplate.title.text
    : t("eventTemplate:titles.create");

  const calendarType = Navigation.getQueryParamByKey(history, QueryParams.type);
  const catalogueType = eventCalendarTypeFromCatalogueParam(calendarType);
  const catalogueDisplay = eventCalendarTypeDisplay[catalogueType];
  const isEngage = !hasEnhancedFeatures(catalogueType);
  const breadcrumbItem: BreadcrumbItem = {
    name: t("eventTemplate:titles.eventCatalogue", {
      type: t(catalogueDisplay.labelKey)
    }),
    onClick: () =>
      Navigation.navigateWithParams(
        history,
        RoutePaths.EventCatalogue,
        QueryParams.type,
        calendarType
      ),
    child: {name: pageTitle}
  };

  useEffect(() => {
    if (isEngage) {
      runInAction(() => {
        eventTemplate.description.setText(
          t("eventTemplate:labels.engageEvent")
        );
        eventTemplate.duration.hours.setNumber(1);
      });
    }
  }, [eventTemplate.description, eventTemplate.duration.hours, isEngage, t]);

  return (
    <div className="event-template">
      <CreateEditForm>
        <CreateEditForm.Header
          hasUnsavedChanges={hasUnsavedChanges}
          breadcrumbItem={breadcrumbItem}
          iconSource={catalogueDisplay.icon}
          haloColor={catalogueDisplay.haloColor}
        >
          <div>
            <ButtonIcon
              type="primary"
              onClick={async () => {
                eventTemplate.setIsDirty(true);
                if (eventTemplate.canPublish) {
                  await onPublish();
                }
              }}
              isDisabled={!eventTemplate.canPublish || isSaving}
              isBusy={isSaving}
            >
              {t("translation:actions.publish")}
            </ButtonIcon>
          </div>
        </CreateEditForm.Header>
        <CreateEditForm.Body>
          {isLoading ||
          (categoriesLoading && eventTemplate.category.items.length === 0) ? (
            <Skeleton className="placeholder" />
          ) : (
            <>
              <div className="col-6 ">
                <Label
                  text={t("eventTemplate:labels.title")}
                  isRequired={true}
                ></Label>
                <TextBox
                  errorText={
                    !eventTemplate.title.isValid
                      ? t("eventTemplate:errors.title")
                      : ""
                  }
                  type="text"
                  value={eventTemplate.title}
                  placeholder={t("eventTemplate:placeholders.title")}
                />
              </div>
              <div>
                <SingleSelect
                  label={t("eventTemplate:labels.category")}
                  validationMessage={t("eventTemplate:errors:category")}
                  items={eventTemplate.category}
                />
              </div>
              {!isEngage && (
                <div>
                  <CheckBox
                    value={eventTemplate.isVillageFavourite.value}
                    onValueChange={(value) =>
                      eventTemplate?.isVillageFavourite.setBoolean(value)
                    }
                    helpText={t("eventTemplate:labels.featureHelpText")}
                    label={t("eventTemplate:labels.isVillageFavourites")}
                  />
                </div>
              )}
              {!isEngage && (
                <>
                  <div className="form-separator" />
                  <div>
                    <EventTemplateDurationEdit
                      eventTemplateDuration={eventTemplate.duration}
                    />
                  </div>
                  <div className="form-separator" />
                  <div>
                    <Label
                      text={t("eventTemplate:labels.description")}
                      isRequired={true}
                    ></Label>
                    <TextBox
                      errorText={
                        !eventTemplate.description.isValid
                          ? t("eventTemplate:errors.description")
                          : ""
                      }
                      type="textarea"
                      value={eventTemplate.description}
                      placeholder={t("eventTemplate:placeholders.description")}
                      rows={4}
                    />
                  </div>
                  <div>
                    <ImageSelector
                      imageLoading={imageLoading}
                      value={eventTemplate.image}
                      validationMessage={getImageValidationMessage()}
                      label={t("eventTemplate:labels.image")}
                      onImageSelect={(file) => onImageSelected(file)}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </CreateEditForm.Body>
      </CreateEditForm>
    </div>
  );
};

export default observer(CreateEditEventTemplateTemplate);
